import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { ADMIN_NAV_LINKS } from "../Sidebar/PlatformAdminSidebar";
import { COMPANY_ADMIN_NAV_LINKS } from "../Sidebar/CompanyAdminSidebar";
import { COMPANY_USER_NAV_LINKS } from "../Sidebar/CompanyUserSidebar";
import { UNIVERSITY_ADMIN_NAV_LINKS } from "../Sidebar/UniversityAdminSidebar";
import { UNIVERSITY_STUDENT_NAV_LINKS } from "../Sidebar/UniversityStudentSidebar";

export default function SidebarLayoutFooter() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const brandLogo =
    theme === "dark"
      ? "/brand/quantum-logo-dark.png"
      : "/brand/quantum-logo.png";

  // Determine platform navigation links based on user role
  const getPlatformNavLinks = () => {
    if (!user?.roles) return [];

    const normalizedRoles = user.roles.map((r) => r.toLowerCase());

    if (normalizedRoles.includes("platform_admin")) {
      return ADMIN_NAV_LINKS.map((link) => ({
        label: link.label,
        href: link.path,
      }));
    } else if (normalizedRoles.includes("company_admin")) {
      return COMPANY_ADMIN_NAV_LINKS.map((link) => ({
        label: link.label,
        href: link.path,
      }));
    } else if (normalizedRoles.includes("company_user")) {
      return COMPANY_USER_NAV_LINKS.filter((link) => {
        if (link.path === "/my-team") {
          return Array.isArray(user?.team_ids) && user.team_ids.length > 0;
        }
        return true;
      }).map((link) => ({
        label: link.label,
        href: link.path,
      }));
    } else if (normalizedRoles.includes("university_admin")) {
      return UNIVERSITY_ADMIN_NAV_LINKS.map((link) => ({
        label: link.label,
        href: link.path,
      }));
    } else if (normalizedRoles.includes("university_student")) {
      return UNIVERSITY_STUDENT_NAV_LINKS.map((link) => ({
        label: link.label,
        href: link.path,
      }));
    }

    // Default for other roles
    return [
      { label: t("footer.platform_links.dashboard"), href: "/home" },
      { label: t("footer.platform_links.boxes"), href: "/boxes" },
      { label: t("footer.platform_links.topology"), href: "/topology" },
    ];
  };

  const footerLinks = [
    {
      title: t("footer.sections.platform"),
      links: getPlatformNavLinks(),
    },
    {
      title: t("footer.sections.resources"),
      links: [
        { label: t("footer.resources_links.documentation"), href: "#" },
        { label: t("footer.resources_links.api_reference"), href: "#" },
        { label: t("footer.resources_links.support"), href: "#" },
      ],
    },
    {
      title: t("footer.sections.company"),
      links: [
        { label: t("footer.company_links.about_us"), href: "#" },
        { label: t("footer.company_links.contact"), href: "#" },
        { label: t("footer.company_links.privacy_policy"), href: "#" },
      ],
    },
  ];

  return (
    <footer className="bg-(--color-bg)/50 border-t border-(--color-border) mt-auto">
      <div className="max-w-400 mx-auto px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/home" className="flex items-center gap-2.5 mb-4">
              <img
                src={brandLogo}
                alt={t("footer.brand")}
                className="h-9 w-9 object-contain"
              />
              <span className="font-bold text-base text-(--color-text-primary) tracking-wide">
                {t("footer.brand")}
              </span>
            </Link>
            <p className="text-xs text-(--color-text-muted) dark:text-gray-400 leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Links */}
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className="text-(--color-text-primary) dark:text-gray-300 font-medium mb-4">
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-xs text-(--color-text-muted) dark:text-gray-400 hover:text-(--color-primary) transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="mt-10 pt-6 border-t border-(--color-border) flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-(--color-text-muted) dark:text-gray-400">
            {t("footer.bottom.copyright", { year: currentYear })}
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="#"
              className="text-xs text-(--color-text-muted) dark:text-gray-400 hover:text-(--color-primary) transition-colors"
            >
              {t("footer.bottom.terms_of_service")}
            </Link>
            <Link
              to="#"
              className="text-xs text-(--color-text-muted) dark:text-gray-400 hover:text-(--color-primary) transition-colors"
            >
              {t("footer.bottom.privacy_policy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
